/**
 * Lightweight client-side i18n. Mappedin's own `mapData.changeLanguage()`
 * only translates map data (exhibitor/location names) and depends on the
 * venue having multilingual content configured in Mappedin admin — it says
 * nothing about kiosk UI chrome. This dictionary covers everything the
 * kiosk itself renders (search, turn-by-turn, toggles, idle/error states),
 * and `setLocale()` also calls `mapData.changeLanguage()` best-effort so
 * exhibitor names follow along when the venue has that data.
 */

export type Locale = "en" | "es" | "fr" | "de" | "ar";

export interface LocaleInfo {
  code: Locale;
  nativeLabel: string;
  rtl: boolean;
}

export const SUPPORTED_LOCALES: LocaleInfo[] = [
  { code: "en", nativeLabel: "English", rtl: false },
  { code: "es", nativeLabel: "Español", rtl: false },
  { code: "fr", nativeLabel: "Français", rtl: false },
  { code: "de", nativeLabel: "Deutsch", rtl: false },
  { code: "ar", nativeLabel: "العربية", rtl: true },
];

export interface Dictionary {
  reset: string;
  chooseDirections: string;
  showSteps: string;
  hideSteps: string;
  wayfinding: string;
  from: string;
  to: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  getDirections: string;
  searchDestination: string;
  emergencyExit: string;
  nearestExit: string;
  nearestExitHint: string;
  noExitRoute: string;
  chooseStartForExit: string;
  nearestFirstFrom: string;
  exitsNearestFirst: string;
  nearestTag: string;
  onYourWay: string;
  clearRoute: string;
  listingTab: string;
  keyboardTab: string;
  directionsHere: string;
  startFromHere: string;
  allStands: string;
  exhibitor: string;
  alreadyAt: string;
  noRouteFound: string;
  checkConnectedAisles: string;
  metersSuffix: string;
  stepsApprox: string;
  minWalk: string;
  thisKioskSuffix: string;
  loadingVenueMap: string;
  loadingCalibratingFloors: string;
  loadingPlacingStands: string;
  loadingMappingRoutes: string;
  loadingPolishing3D: string;
  loadingOptimizingTour: string;
  loadingAlmostThere: string;
  couldNotLoadMap: string;
  tapToStart: string;
  noExhibitorsYet: string;
  couldNotCalculateDirections: string;
  detourSuffix: string;
  lockRotation: string;
  unlockRotation: string;
  floorLevel: string;
  occupancyLow: string;
  occupancyModerate: string;
  occupancyBusy: string;
  dirHeadOut: string;
  dirContinueStraight: string;
  dirTurnRight: string;
  dirBearRight: string;
  dirTurnLeft: string;
  dirBearLeft: string;
  dirTurnAround: string;
  dirContinueGeneric: string;
  dirTakeThe: string;
  dirExitThe: string;
  dirArrived: string;
  dirStep: string;
  online: string;
  offline: string;
  scanToContinue: string;
  scanToContinueHint: string;
  liveLocationAcquiring: string;
  liveLocationActive: string;
  liveLocationDenied: string;
  liveLocationUnavailable: string;
  currentLocation: string;
  currentKioskLocation: string;
}

const DICTIONARIES: Record<Locale, Dictionary> = {
  en: {
    reset: "Reset",
    chooseDirections: "Choose Directions",
    showSteps: "Show route description",
    hideSteps: "Hide route description",
    wayfinding: "Wayfinding",
    from: "From",
    to: "To",
    fromPlaceholder: "Start location…",
    toPlaceholder: "Destination stand or booth…",
    getDirections: "Get Directions",
    searchDestination: "Where do you want to go?",
    emergencyExit: "Emergency exit",
    nearestExit: "Nearest Exit",
    nearestExitHint: "Closest emergency exit from here",
    noExitRoute: "No route to an emergency exit was found from here.",
    chooseStartForExit: "Choose a starting point first.",
    nearestFirstFrom: "Nearest first · from {{name}}",
    exitsNearestFirst: "Emergency exits · nearest first",
    nearestTag: "Nearest",
    onYourWay: "On Your Way",
    clearRoute: "Clear Route",
    listingTab: "Listing",
    keyboardTab: "Keyboard",
    directionsHere: "Directions Here",
    startFromHere: "Start From Here",
    allStands: "All Stands",
    exhibitor: "Exhibitor",
    alreadyAt: "You are already at {{name}}.",
    noRouteFound: "No route found between stands.",
    checkConnectedAisles: "Check that both stands are connected to aisles.",
    metersSuffix: "{{n}} meters",
    stepsApprox: "≈ {{n}} steps",
    minWalk: "{{n}} min walk",
    thisKioskSuffix: "(This Kiosk)",
    loadingVenueMap: "Loading venue map…",
    loadingCalibratingFloors: "Calibrating floor plan…",
    loadingPlacingStands: "Placing exhibitor stands…",
    loadingMappingRoutes: "Mapping walking routes…",
    loadingPolishing3D: "Polishing the 3D view…",
    loadingOptimizingTour: "Optimizing for a smooth tour…",
    loadingAlmostThere: "Almost there…",
    couldNotLoadMap: "Couldn't load the map. Check network connection and reload.",
    tapToStart: "Touch the screen to find your way",
    noExhibitorsYet: "No exhibitors assigned to a stand yet. Add Locations and place them on Spaces in Mappedin admin.",
    couldNotCalculateDirections: "Could not calculate directions. Please try again.",
    detourSuffix: "+{{n}}s detour",
    lockRotation: "Lock rotation",
    unlockRotation: "Unlock rotation",
    floorLevel: "Floor {{n}}",
    occupancyLow: "Quiet",
    occupancyModerate: "Moderate",
    occupancyBusy: "Busy",
    dirHeadOut: "Head out and start walking{{walk}}",
    dirContinueStraight: "Continue straight{{walk}}",
    dirTurnRight: "Turn right{{walk}}",
    dirBearRight: "Bear slightly right{{walk}}",
    dirTurnLeft: "Turn left{{walk}}",
    dirBearLeft: "Bear slightly left{{walk}}",
    dirTurnAround: "Turn around{{walk}}",
    dirContinueGeneric: "Continue{{walk}}",
    dirTakeThe: "Take the {{kind}}{{dir}}{{toFloor}}",
    dirExitThe: "Exit the {{kind}}{{walk}}",
    dirArrived: "You have arrived at your destination",
    dirStep: "Step {{n}}{{walk}}",
    online: "Online",
    offline: "Offline",
    scanToContinue: "Scan to continue on your phone",
    scanToContinueHint: "This opens the same directions on your device — only works while your phone can reach this kiosk's network.",
    liveLocationAcquiring: "Locating…",
    liveLocationActive: "Live · ±{{n}} m",
    liveLocationDenied: "Location access denied",
    liveLocationUnavailable: "Location unavailable",
    currentLocation: "Current Location",
    currentKioskLocation: "Current {name}",
  },
  es: {
    reset: "Reiniciar",
    chooseDirections: "Elegir Ruta",
    showSteps: "Mostrar descripción de la ruta",
    hideSteps: "Ocultar descripción de la ruta",
    wayfinding: "Orientación",
    from: "Desde",
    to: "Hasta",
    fromPlaceholder: "Ubicación de partida…",
    toPlaceholder: "Stand o caseta de destino…",
    getDirections: "Obtener Ruta",
    searchDestination: "¿A dónde quiere ir?",
    emergencyExit: "Salida de emergencia",
    nearestExit: "Salida más cercana",
    nearestExitHint: "Salida de emergencia más cercana desde aquí",
    noExitRoute: "No se encontró ninguna ruta a una salida de emergencia desde aquí.",
    chooseStartForExit: "Elija primero un punto de partida.",
    nearestFirstFrom: "Más cercano primero · desde {{name}}",
    exitsNearestFirst: "Salidas de emergencia · la más cercana primero",
    nearestTag: "Más cercano",
    onYourWay: "En tu Camino",
    clearRoute: "Borrar Ruta",
    listingTab: "Lista",
    keyboardTab: "Teclado",
    directionsHere: "Ir Aquí",
    startFromHere: "Empezar Aquí",
    allStands: "Todos los Stands",
    
    exhibitor: "Expositor",
    alreadyAt: "Ya estás en {{name}}.",
    noRouteFound: "No se encontró ruta entre los stands.",
    checkConnectedAisles: "Comprueba que ambos stands estén conectados a pasillos.",
    metersSuffix: "{{n}} metros",
    stepsApprox: "≈ {{n}} pasos",
    minWalk: "{{n}} min a pie",
    thisKioskSuffix: "(Este Quiosco)",
    loadingVenueMap: "Cargando mapa del recinto…",
    loadingCalibratingFloors: "Calibrando el plano del piso…",
    loadingPlacingStands: "Colocando los stands de expositores…",
    loadingMappingRoutes: "Trazando las rutas a pie…",
    loadingPolishing3D: "Puliendo la vista 3D…",
    loadingOptimizingTour: "Optimizando para un recorrido fluido…",
    loadingAlmostThere: "Casi listo…",
    couldNotLoadMap: "No se pudo cargar el mapa. Comprueba la conexión y recarga.",
    tapToStart: "Toca la pantalla para encontrar tu camino",
    noExhibitorsYet: "Aún no hay expositores asignados a un stand. Agrega ubicaciones en el panel de Mappedin.",
    couldNotCalculateDirections: "No se pudo calcular la ruta. Inténtalo de nuevo.",
    detourSuffix: "+{{n}}s de desvío",
    lockRotation: "Bloquear rotación",
    unlockRotation: "Desbloquear rotación",
    floorLevel: "Piso {{n}}",
    occupancyLow: "Tranquilo",
    occupancyModerate: "Moderado",
    occupancyBusy: "Concurrido",
    dirHeadOut: "Sal y comienza a caminar{{walk}}",
    dirContinueStraight: "Continúa recto{{walk}}",
    dirTurnRight: "Gira a la derecha{{walk}}",
    dirBearRight: "Mantente ligeramente a la derecha{{walk}}",
    dirTurnLeft: "Gira a la izquierda{{walk}}",
    dirBearLeft: "Mantente ligeramente a la izquierda{{walk}}",
    dirTurnAround: "Da la vuelta{{walk}}",
    dirContinueGeneric: "Continúa{{walk}}",
    dirTakeThe: "Toma {{kind}}{{dir}}{{toFloor}}",
    dirExitThe: "Sal de {{kind}}{{walk}}",
    dirArrived: "Has llegado a tu destino",
    dirStep: "Paso {{n}}{{walk}}",
    online: "En línea",
    offline: "Sin conexión",
    scanToContinue: "Escanea para continuar en tu teléfono",
    scanToContinueHint: "Esto abre las mismas indicaciones en tu dispositivo — solo funciona mientras tu teléfono pueda conectarse a la red de este quiosco.",
    liveLocationAcquiring: "Ubicando…",
    liveLocationActive: "En vivo · ±{{n}} m",
    liveLocationDenied: "Acceso a la ubicación denegado",
    liveLocationUnavailable: "Ubicación no disponible",
    currentLocation: "Ubicación Actual",
    currentKioskLocation: "{name} (actual)",
  },
  fr: {
    reset: "Réinitialiser",
    chooseDirections: "Choisir l'itinéraire",
    showSteps: "Afficher la description de l'itinéraire",
    hideSteps: "Masquer la description de l'itinéraire",
    wayfinding: "Orientation",
    from: "De",
    to: "À",
    fromPlaceholder: "Lieu de départ…",
    toPlaceholder: "Stand ou emplacement de destination…",
    getDirections: "Obtenir l'Itinéraire",
    searchDestination: "Où souhaitez-vous aller ?",
    emergencyExit: "Sortie de secours",
    nearestExit: "Sortie la plus proche",
    nearestExitHint: "Sortie de secours la plus proche d’ici",
    noExitRoute: "Aucun itinéraire vers une sortie de secours n’a été trouvé depuis ici.",
    chooseStartForExit: "Choisissez d’abord un point de départ.",
    nearestFirstFrom: "Le plus proche d’abord · depuis {{name}}",
    exitsNearestFirst: "Sorties de secours · la plus proche d’abord",
    nearestTag: "Le plus proche",
    onYourWay: "Sur Votre Chemin",
    clearRoute: "Effacer l'Itinéraire",
    listingTab: "Liste",
    keyboardTab: "Clavier",
    directionsHere: "Itinéraire Ici",
    startFromHere: "Partir d'Ici",
    allStands: "Tous les Stands",
    
    exhibitor: "Exposant",
    alreadyAt: "Vous êtes déjà à {{name}}.",
    noRouteFound: "Aucun itinéraire trouvé entre les stands.",
    checkConnectedAisles: "Vérifiez que les deux stands sont reliés à des allées.",
    metersSuffix: "{{n}} mètres",
    stepsApprox: "≈ {{n}} pas",
    minWalk: "{{n}} min à pied",
    thisKioskSuffix: "(Ce Kiosque)",
    loadingVenueMap: "Chargement du plan du site…",
    loadingCalibratingFloors: "Calibrage du plan d'étage…",
    loadingPlacingStands: "Placement des stands des exposants…",
    loadingMappingRoutes: "Tracé des itinéraires à pied…",
    loadingPolishing3D: "Peaufinage de la vue 3D…",
    loadingOptimizingTour: "Optimisation pour une visite fluide…",
    loadingAlmostThere: "Presque prêt…",
    couldNotLoadMap: "Impossible de charger la carte. Vérifiez la connexion et rechargez.",
    tapToStart: "Touchez l'écran pour trouver votre chemin",
    noExhibitorsYet: "Aucun exposant n'est encore assigné à un stand. Ajoutez des lieux dans l'administration Mappedin.",
    couldNotCalculateDirections: "Impossible de calculer l'itinéraire. Veuillez réessayer.",
    detourSuffix: "+{{n}}s de détour",
    lockRotation: "Verrouiller la rotation",
    unlockRotation: "Déverrouiller la rotation",
    floorLevel: "Étage {{n}}",
    occupancyLow: "Calme",
    occupancyModerate: "Modéré",
    occupancyBusy: "Fréquenté",
    dirHeadOut: "Sortez et commencez à marcher{{walk}}",
    dirContinueStraight: "Continuez tout droit{{walk}}",
    dirTurnRight: "Tournez à droite{{walk}}",
    dirBearRight: "Serrez légèrement à droite{{walk}}",
    dirTurnLeft: "Tournez à gauche{{walk}}",
    dirBearLeft: "Serrez légèrement à gauche{{walk}}",
    dirTurnAround: "Faites demi-tour{{walk}}",
    dirContinueGeneric: "Continuez{{walk}}",
    dirTakeThe: "Prenez {{kind}}{{dir}}{{toFloor}}",
    dirExitThe: "Sortez de {{kind}}{{walk}}",
    dirArrived: "Vous êtes arrivé à destination",
    dirStep: "Étape {{n}}{{walk}}",
    online: "En ligne",
    offline: "Hors ligne",
    scanToContinue: "Scannez pour continuer sur votre téléphone",
    scanToContinueHint: "Cela ouvre les mêmes indications sur votre appareil — ne fonctionne que si votre téléphone peut atteindre le réseau de ce kiosque.",
    liveLocationAcquiring: "Localisation…",
    liveLocationActive: "En direct · ±{{n}} m",
    liveLocationDenied: "Accès à la position refusé",
    liveLocationUnavailable: "Position indisponible",
    currentLocation: "Position Actuelle",
    currentKioskLocation: "{name} (actuel)",
  },
  de: {
    reset: "Zurücksetzen",
    chooseDirections: "Route wählen",
    showSteps: "Routenbeschreibung anzeigen",
    hideSteps: "Routenbeschreibung ausblenden",
    wayfinding: "Wegeführung",
    from: "Von",
    to: "Nach",
    fromPlaceholder: "Startort…",
    toPlaceholder: "Ziel-Stand oder Messestand…",
    getDirections: "Route Anzeigen",
    searchDestination: "Wohin möchten Sie?",
    emergencyExit: "Notausgang",
    nearestExit: "Nächster Ausgang",
    nearestExitHint: "Nächster Notausgang von hier",
    noExitRoute: "Von hier wurde keine Route zu einem Notausgang gefunden.",
    chooseStartForExit: "Wählen Sie zuerst einen Startpunkt.",
    nearestFirstFrom: "Nächste zuerst · ab {{name}}",
    exitsNearestFirst: "Notausgänge · nächster zuerst",
    nearestTag: "Am nächsten",
    onYourWay: "Auf Ihrem Weg",
    clearRoute: "Route Löschen",
    listingTab: "Liste",
    keyboardTab: "Tastatur",
    directionsHere: "Route Hierher",
    startFromHere: "Von Hier Starten",
    allStands: "Alle Stände",
    
    exhibitor: "Aussteller",
    alreadyAt: "Sie sind bereits bei {{name}}.",
    noRouteFound: "Keine Route zwischen den Ständen gefunden.",
    checkConnectedAisles: "Stellen Sie sicher, dass beide Stände mit Gängen verbunden sind.",
    metersSuffix: "{{n}} Meter",
    stepsApprox: "≈ {{n}} Schritte",
    minWalk: "{{n}} Min. zu Fuß",
    thisKioskSuffix: "(Dieser Kiosk)",
    loadingVenueMap: "Standplan wird geladen…",
    loadingCalibratingFloors: "Grundriss wird kalibriert…",
    loadingPlacingStands: "Ausstellerstände werden platziert…",
    loadingMappingRoutes: "Gehwege werden berechnet…",
    loadingPolishing3D: "3D-Ansicht wird verfeinert…",
    loadingOptimizingTour: "Optimierung für einen reibungslosen Rundgang…",
    loadingAlmostThere: "Gleich geschafft…",
    couldNotLoadMap: "Karte konnte nicht geladen werden. Netzwerkverbindung prüfen und neu laden.",
    tapToStart: "Bildschirm berühren, um den Weg zu finden",
    noExhibitorsYet: "Noch keine Aussteller einem Stand zugewiesen. Standorte im Mappedin-Admin hinzufügen.",
    couldNotCalculateDirections: "Route konnte nicht berechnet werden. Bitte erneut versuchen.",
    detourSuffix: "+{{n}}s Umweg",
    lockRotation: "Rotation sperren",
    unlockRotation: "Rotation entsperren",
    floorLevel: "Etage {{n}}",
    occupancyLow: "Ruhig",
    occupancyModerate: "Mäßig",
    occupancyBusy: "Belebt",
    dirHeadOut: "Losgehen und weiterlaufen{{walk}}",
    dirContinueStraight: "Geradeaus weiter{{walk}}",
    dirTurnRight: "Rechts abbiegen{{walk}}",
    dirBearRight: "Leicht rechts halten{{walk}}",
    dirTurnLeft: "Links abbiegen{{walk}}",
    dirBearLeft: "Leicht links halten{{walk}}",
    dirTurnAround: "Umdrehen{{walk}}",
    dirContinueGeneric: "Weiter{{walk}}",
    dirTakeThe: "Nehmen Sie {{kind}}{{dir}}{{toFloor}}",
    dirExitThe: "Verlassen Sie {{kind}}{{walk}}",
    dirArrived: "Sie haben Ihr Ziel erreicht",
    dirStep: "Schritt {{n}}{{walk}}",
    online: "Online",
    offline: "Offline",
    scanToContinue: "Scannen, um auf Ihrem Handy fortzufahren",
    scanToContinueHint: "Dies öffnet dieselbe Wegbeschreibung auf Ihrem Gerät — funktioniert nur, wenn Ihr Handy das Netzwerk dieses Kiosks erreichen kann.",
    liveLocationAcquiring: "Standort wird ermittelt…",
    liveLocationActive: "Live · ±{{n}} m",
    liveLocationDenied: "Standortzugriff verweigert",
    liveLocationUnavailable: "Standort nicht verfügbar",
    currentLocation: "Aktueller Standort",
    currentKioskLocation: "Aktuell: {name}",
  },
  ar: {
    reset: "إعادة تعيين",
    chooseDirections: "اختر الاتجاهات",
    showSteps: "إظهار وصف المسار",
    hideSteps: "إخفاء وصف المسار",
    wayfinding: "الاتجاهات",
    from: "من",
    to: "إلى",
    fromPlaceholder: "موقع الانطلاق…",
    toPlaceholder: "الجناح أو الموقع الوجهة…",
    getDirections: "احصل على الاتجاهات",
    searchDestination: "إلى أين تريد الذهاب؟",
    emergencyExit: "مخرج طوارئ",
    nearestExit: "أقرب مخرج",
    nearestExitHint: "أقرب مخرج طوارئ من هنا",
    noExitRoute: "لم يتم العثور على مسار إلى مخرج طوارئ من هنا.",
    chooseStartForExit: "اختر نقطة البداية أولاً.",
    nearestFirstFrom: "الأقرب أولاً · من {{name}}",
    exitsNearestFirst: "مخارج الطوارئ · الأقرب أولاً",
    nearestTag: "الأقرب",
    onYourWay: "في طريقك",
    clearRoute: "مسح المسار",
    listingTab: "القائمة",
    keyboardTab: "لوحة المفاتيح",
    directionsHere: "الاتجاهات إلى هنا",
    startFromHere: "ابدأ من هنا",
    allStands: "كل الأجنحة",
        exhibitor: "عارض",
    alreadyAt: "أنت بالفعل في {{name}}.",
    noRouteFound: "لم يتم العثور على مسار بين الأجنحة.",
    checkConnectedAisles: "تحقق من أن كلا الجناحين متصلان بالممرات.",
    metersSuffix: "{{n}} متر",
    stepsApprox: "≈ {{n}} خطوة",
    minWalk: "{{n}} دقيقة سيرًا",
    thisKioskSuffix: "(هذا الكشك)",
    loadingVenueMap: "جارٍ تحميل خريطة المكان…",
    loadingCalibratingFloors: "جارٍ معايرة مخطط الطابق…",
    loadingPlacingStands: "جارٍ وضع أجنحة العارضين…",
    loadingMappingRoutes: "جارٍ رسم مسارات المشي…",
    loadingPolishing3D: "جارٍ صقل العرض ثلاثي الأبعاد…",
    loadingOptimizingTour: "جارٍ التحسين لجولة سلسة…",
    loadingAlmostThere: "على وشك الانتهاء…",
    couldNotLoadMap: "تعذر تحميل الخريطة. تحقق من الاتصال بالشبكة وأعد التحميل.",
    tapToStart: "المس الشاشة للعثور على طريقك",
    noExhibitorsYet: "لم يتم تعيين عارضين لأي جناح بعد. أضف المواقع في لوحة إدارة Mappedin.",
    couldNotCalculateDirections: "تعذر حساب الاتجاهات. حاول مرة أخرى.",
    detourSuffix: "+{{n}} ثانية التفاف",
    lockRotation: "قفل الدوران",
    unlockRotation: "إلغاء قفل الدوران",
    floorLevel: "الطابق {{n}}",
    occupancyLow: "هادئ",
    occupancyModerate: "متوسط",
    occupancyBusy: "مزدحم",
    dirHeadOut: "اخرج وابدأ المشي{{walk}}",
    dirContinueStraight: "استمر مباشرة{{walk}}",
    dirTurnRight: "انعطف يمينًا{{walk}}",
    dirBearRight: "اتجه قليلاً إلى اليمين{{walk}}",
    dirTurnLeft: "انعطف يسارًا{{walk}}",
    dirBearLeft: "اتجه قليلاً إلى اليسار{{walk}}",
    dirTurnAround: "استدر{{walk}}",
    dirContinueGeneric: "استمر{{walk}}",
    dirTakeThe: "خذ {{kind}}{{dir}}{{toFloor}}",
    dirExitThe: "اخرج من {{kind}}{{walk}}",
    dirArrived: "لقد وصلت إلى وجهتك",
    dirStep: "الخطوة {{n}}{{walk}}",
    online: "متصل",
    offline: "غير متصل",
    scanToContinue: "امسح الرمز للمتابعة على هاتفك",
    scanToContinueHint: "سيؤدي هذا إلى فتح نفس الاتجاهات على جهازك — يعمل فقط بينما يستطيع هاتفك الوصول إلى شبكة هذا الكشك.",
    liveLocationAcquiring: "جارٍ تحديد الموقع…",
    liveLocationActive: "مباشر · ±{{n}} م",
    liveLocationDenied: "تم رفض الوصول إلى الموقع",
    liveLocationUnavailable: "الموقع غير متاح",
    currentLocation: "الموقع الحالي",
    currentKioskLocation: "{name} (الحالي)",
  },
};

const STORAGE_KEY = "mappedin-locale";
let currentLocale: Locale = "en";
const listeners = new Set<(locale: Locale) => void>();

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = vars[key];
    return value === undefined ? match : String(value);
  });
}

export function t(key: keyof Dictionary, vars?: Record<string, string | number>): string {
  const template = DICTIONARIES[currentLocale][key] ?? DICTIONARIES.en[key];
  return interpolate(template, vars);
}

export function getLocale(): Locale {
  return currentLocale;
}

export function getLocaleInfo(locale: Locale): LocaleInfo {
  return SUPPORTED_LOCALES.find((l) => l.code === locale) ?? SUPPORTED_LOCALES[0];
}

export function onLocaleChange(fn: (locale: Locale) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function applyStaticTranslations(): void {
  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const key = el.dataset.i18n as keyof Dictionary | undefined;
    if (key && DICTIONARIES.en[key] !== undefined) el.textContent = t(key);
  });
  document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-i18n-placeholder]").forEach((el) => {
    const key = el.dataset.i18nPlaceholder as keyof Dictionary | undefined;
    if (key && DICTIONARIES.en[key] !== undefined) el.placeholder = t(key);
  });
}

function applyDocumentDirection(locale: Locale): void {
  const info = getLocaleInfo(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = info.rtl ? "rtl" : "ltr";
}

/** Reads the persisted locale (or defaults to "en") and applies it before first paint. */
export function initLocale(): Locale {
  const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
  currentLocale = saved && DICTIONARIES[saved] ? saved : "en";
  applyDocumentDirection(currentLocale);
  applyStaticTranslations();
  return currentLocale;
}

/** Switches the active locale, updates the DOM, persists the choice, and notifies subscribers. */
export function setLocale(locale: Locale): void {
  if (!DICTIONARIES[locale] || locale === currentLocale) return;
  currentLocale = locale;
  localStorage.setItem(STORAGE_KEY, locale);
  applyDocumentDirection(locale);
  applyStaticTranslations();
  for (const fn of listeners) fn(locale);
}
